import { ActionTree } from 'vuex';
import { WorkshopsScheduleState } from './types';
import { WeekSchedule, WeekDay, Location, Room, Workshop, Timeline } from '../models/weekScheduleModels';
import { Registration } from '../models/formModels';
import { EventTicket } from '../models/EventRegistration';
import { AvailableTicketType } from '../models/formModels';
import { SheduleBoardWeekDaysAdjuster } from '../models/logic/SheduleBoardWeekDaysAdjuster';
import { TicketsManager } from '../models/logic/TicketsManager';
import { RootState } from '@/vuex/types';
import OdataResource from '@/modules/api/OdataResource';
import RestResource from '@/modules/api/RestResource';
import { hourToNumber, numberToHour } from '@/utils/time';
import { defaultWeekSchedule } from '@/modules/workshops-schedule/store/dafaults';

export const actions: ActionTree<WorkshopsScheduleState, RootState> = {
    setLoading({ commit }, loading): void {
        commit('storeLoading', loading);
    },
    showGuideline({ commit }): void {
        commit('storeIsGuidelineVisible', true);
    },
    hideGuideline({ commit }): void {
        commit('storeIsGuidelineVisible', false);
    },
    setWeekSchedule({ commit }, weekSchedule): void {
        commit('storeWeekSchedule', weekSchedule);
    },
    setFormDetails({ commit }, { eventRegistrationId, eventRegistrationGuid, recommendedBy }): void {
        if (eventRegistrationId) commit('storeEventRegistrationId', eventRegistrationId);
        if (eventRegistrationGuid) commit('storeEventRegistrationGuid', eventRegistrationGuid);
        if (recommendedBy) commit('storeRecommendedBy', recommendedBy);
    },
    toogleSelectedTicket({ commit, state }, ticketId): void {
        if (!ticketId) {
            return;
        }

        const tickets = state.selsctedTicketsIds;
        const workshopsInTicket = new TicketsManager().getAllWorkshopsInTicket(ticketId, state.weekSchedule.WeekDays);

        const isAvailable = state.availableTicketTypes.filter(x => x.Id === ticketId && x.IsAvailable).length > 0;
        if (tickets.includes(ticketId)) {
            commit('unselectTicket', ticketId);
            commit('unselectWorkshops', workshopsInTicket);
        } else if (isAvailable) {
            commit('selectTicket', ticketId);
            commit('selectWorkshops', workshopsInTicket);
        }
    },
    tooglSelectedWorkshopId({ commit, state }, workshop: Workshop): void {
        if (!workshop || !workshop.HasDedicatedTicket) {
            return;
        }

        const workshopsInWorkshopTicket = new TicketsManager().getAllWorkshopsInWorkshopTicket(
            workshop,
            state.weekSchedule.WeekDays,
        );
        if (state.selectedWorkshopsIds?.includes(workshop.Id ?? 0)) {
            commit('unselectWorkshops', workshopsInWorkshopTicket);
        } else {
            commit('selectWorkshops', workshopsInWorkshopTicket);
        }
    },
    async loadFormStateForRegistration({ commit, state, rootState }): Promise<Registration | null> {
        if (!state.eventRegistrationId || !state.eventRegistrationGuid) {
            return null;
        }
        const EventRegistrationForms =
            rootState.configuration?.environment.apiUrl + `/registrations/EventRegistrationForms`;
        const eventRegistrationFormsResource: RestResource<Registration> = new RestResource<Registration>(
            EventRegistrationForms,
            function() {
                return;
            },
            function() {
                return;
            },
            function(error: any) {
                console.log(error);
            },
        );

        return eventRegistrationFormsResource.getResource(
            `${state.eventRegistrationId}/${state.eventRegistrationGuid}`,
        );
    },
    async loadParticipantEventTickets({ commit, state, rootState }): Promise<EventTicket[] | undefined> {
        const path = `/registrations/EventTickets/forParticipantWithRegistration/${state.eventRegistrationGuid}`;
        const EventTicketsUrl = rootState.configuration?.environment.apiUrl + `${path}`;
        const EventTicketsResource: OdataResource<EventTicket> = new OdataResource<EventTicket>(
            EventTicketsUrl,
            function() {
                commit('storeLoading', true);
            },
            function() {
                commit('storeLoading', false);
            },
            function(error: any) {
                console.log(error);
            },
        );
        EventTicketsResource.$on('resourcesLoaded', function(resources: any) {
            const eventTickets: EventTicket[] = resources;
            if (eventTickets !== undefined && eventTickets !== null && eventTickets.length) {
                commit('storeEventTickets', eventTickets);
            }
        });

        return EventTicketsResource.getResources('');
    },
    async fetchAvaiableTicketTypes(
        { commit, state, rootState },
        eventTicketGuid: string | null = null,
    ): Promise<AvailableTicketType[] | undefined> {
        const eventId = rootState.form?.eventId;
        const path = eventTicketGuid
            ? `forExchangeFrom/${eventTicketGuid}`
            : state.eventRegistrationGuid
            ? `${eventId}/${state.eventRegistrationGuid}`
            : state.recommendedBy
            ? `forRecommendedBy/${state.recommendedBy}/${eventId}`
            : `${eventId}?showNotVisible=true`;
        const AvailableTicketTypes =
            rootState.configuration?.environment.apiUrl + `/forms/AvailableTicketTypes/${path}`;
        const availableTicketTypesResource: RestResource<AvailableTicketType> = new RestResource<AvailableTicketType>(
            AvailableTicketTypes,
            function() {
                commit('storeLoading', true);
            },
            function() {
                commit('storeLoading', false);
            },
            function(error: any) {
                console.log(error);
            },
        );
        availableTicketTypesResource.$on('resourcesLoaded', function(resources: any) {
            const availableTicketTypes: AvailableTicketType[] = resources;
            if (availableTicketTypes !== undefined && availableTicketTypes !== null && availableTicketTypes.length) {
                commit('storeAvailableTicketTypes', availableTicketTypes);
            }
        });

        return await availableTicketTypesResource.getResources('');
    },
    async fetchCurrentWeekDays({ commit, state, rootState }): Promise<void> {
        const url = rootState.configuration?.environment.apiUrl + '/odata/schedules/CurrentWeekDays';
        const currentWeekDaysResource: OdataResource<WeekDay> = new OdataResource<WeekDay>(
            url,
            function() {
                commit('storeLoading', true);
            },
            function() {
                commit('storeLoading', false);
            },
            function(error: any) {
                console.log(error);
            },
            function(resources: any) {
                const weekDays: WeekDay[] = resources;
                // const weekDays: WeekDay[] = defaultWeekSchedule().WeekDays;
                const helper = new SheduleBoardWeekDaysAdjuster();
                const timeUnits = helper.getTimeUnits(weekDays);
                weekDays.forEach(weekDay => (weekDay.Timeline = helper.createTimelineFor(weekDay, timeUnits)));

                commit('storeTimelineStartTime', timeUnits[0].Value);
                commit(
                    'storeTimelineEndTime',
                    numberToHour(hourToNumber(timeUnits[timeUnits.length - 1].Value) + helper.timeUnitDuration()),
                );
                commit('storeTimelineTimeUnits', timeUnits);
                helper.setBufferingWorkshops(weekDays, state.timeline);
                commit('storeWeekDays', weekDays);
                return;
            },
        );

        await currentWeekDaysResource.getResources('');
    },
    async filterWeekDays({ commit, state, rootState }): Promise<void> {
        // state.weekSchedule.WeekDays.forEach(weekDay => {
        //     const filteredWeekDay: WeekDay = {
        //         Id: weekDay.Id,
        //         Name: weekDay.Name,
        //         Timeline: {
        //             Title: weekDay.Timeline.Title,
        //             TimelineStartTime: weekDay.Timeline.TimelineStartTime,
        //             TimelineEndTime: weekDay.Timeline.TimelineEndTime,
        //             TimeUnits: weekDay.Timeline.TimeUnits,
        //         },
        //         Locations: [],
        //     };
        //     weekDay.Locations.filter(x => x.Rooms.length > 0).forEach(location => {
        //         const filteredLocation: Location = { Name: location.Name, Rooms: [] };
        //         location.Rooms.forEach(room => {
        //             const filteredRoom: Room = { Name: room.Name, Workshops: [] };
        //             const filteredWorkshopTypes = state.weekSchedule.Filters['WorkshopType'].SelectedOptions;
        //             const filteredTitles = state.weekSchedule.Filters['Title'].SelectedOptions;
        //             const filteredInstructorNames = state.weekSchedule.Filters['InstructorName'].SelectedOptions;
        //             room.Workshops.filter(
        //                 x => filteredWorkshopTypes.length == 0 || filteredWorkshopTypes.includes(x.WorkshopType),
        //             )
        //                 .filter(x => filteredTitles.length == 0 || filteredTitles.includes(x.Title))
        //                 .filter(
        //                     x =>
        //                         filteredInstructorNames.length == 0 ||
        //                         filteredInstructorNames.includes(x.InstructorName),
        //                 )
        //                 .forEach(workshop => {
        //                     filteredRoom.Workshops.push(workshop);
        //                 });
        //             filteredLocation.Rooms.push(filteredRoom);
        //             filteredLocation.Rooms.filter(x => x.Workshops.filter(w => w.IsVisible).length === 0).forEach(
        //                 room => {
        //                     filteredLocation.Rooms.splice(filteredLocation.Rooms.indexOf(room), 1);
        //                 },
        //             );
        //         });
        //         filteredWeekDay.Locations.push(filteredLocation);
        //         filteredWeekDay.Locations.filter(x => x.Rooms.length === 0).forEach(location => {
        //             filteredWeekDay.Locations.splice(filteredWeekDay.Locations.indexOf(location), 1);
        //         });
        //     });

        //     state.filteredWeekDays.push(filteredWeekDay);
        //     state.filteredWeekDays
        //         .filter(x => x.Locations.length === 0)
        //         .forEach(weekDay => {
        //             state.filteredWeekDays.splice(state.filteredWeekDays.indexOf(weekDay), 1);
        //         });
        // });

        const helper = new SheduleBoardWeekDaysAdjuster();
        const weekDays = helper.getFilteredWeekDays(rootState.calendar?.filteredEvents ?? []);
        const timeUnits = helper.getTimeUnits(weekDays);
        weekDays.forEach(weekDay => (weekDay.Timeline = helper.createTimelineFor(weekDay, timeUnits)));

        weekDays
            .filter(x => x.Locations.length === 0)
            .forEach(weekDay => {
                weekDays.splice(weekDays.indexOf(weekDay), 1);
            });

        commit(
            'storeFilteredWeekDays',
            weekDays.sort((a, b) => (a.Id > b.Id ? 1 : a.Id < b.Id ? -1 : 0)),
        );

        commit('storeTimelineStartTime', timeUnits[0].Value);
        commit(
            'storeTimelineEndTime',
            numberToHour(hourToNumber(timeUnits[timeUnits.length - 1].Value) + helper.timeUnitDuration()),
        );

        commit('storeTimelineTimeUnits', timeUnits);
        helper.setBufferingWorkshops(weekDays, state.timeline);
    },
};
